import { DataSecurityTct, DataSecurityWithIssuerTct } from '@dealstack/shared/lib/generated';

/**
 * The security a transaction form gates its fields on — the Interest control, the units field and its
 * precision, and the cost label's currency all derive from it.
 *
 * The `securities` list handed to the form is scoped to the page's entity, so it holds that entity's
 * issued securities only. A transaction reached from a HOLDER's page (its Transactions tab, or a
 * details page opened from there) is on a security the list does not contain, and every field derived
 * from the security silently degrades: the Interest dropdown stays read-only, Number of units
 * disappears, and the cost label renders "(undefined)". The loaded transaction always carries its own
 * security, so that is the fallback.
 *
 * `hasNomineeStructure` has to be derived on that fallback: the column on Security is never written,
 * so the nested value is a stale false. The authoritative signal is the ISSUER's flag AND the deal's
 * master switch — the same derivation the backend applies before it stamps the flag onto a securities
 * list (`isNomineeStructureSecurity`).
 */
export const resolveTransactionFormSecurity = ({
	securityId,
	securities,
	transactionSecurity,
	dealHasNomineeStructure,
}: {
	securityId?: string | null;
	securities?: DataSecurityTct[];
	transactionSecurity?: DataSecurityWithIssuerTct;
	dealHasNomineeStructure?: boolean;
}): DataSecurityTct | DataSecurityWithIssuerTct | undefined => {
	const fromList = securities?.find((security) => security.id === securityId);
	if (fromList) {
		return fromList;
	}

	if (!transactionSecurity || transactionSecurity.id !== securityId) {
		return undefined;
	}

	return {
		...transactionSecurity,
		hasNomineeStructure:
			!!dealHasNomineeStructure && !!transactionSecurity.issuer?.hasNomineeStructure,
	};
};
